/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

class PredictiveSearch extends HTMLElement {
    #boundOnChange;
    #boundOnFocus;
    #boundOnFocusOut;
    #boundOnKeyUp;
    #boundOnResultsClick;
    #boundSetDrawerContentMaxHeight;
    constructor() {
        super();
        this.scrollStyle = new SearchPreviewScrollStyle(this);
        this.isComposing = false;
        this.onCompositionStart = () => {
            this.isComposing = true;
            if (this.abortController) this.abortController.abort();
            this.#updateLoading();
            this.close();
        };
        this.onCompositionEnd = () => {
            this.isComposing = false;
            this.#boundOnChange();
        };
        this.type = this.getAttribute("type");
        this.isDropdown = this.hasAttribute("dropdown");
        this.#boundOnChange = debounce(this.#onChange.bind(this), 250);
        this.#boundOnKeyUp = this.#onKeyup.bind(this);
        this.#boundOnFocus = this.#onFocus.bind(this);
        this.#boundOnFocusOut = this.#onFocusOut.bind(this);
        this.#boundOnResultsClick = this.#onResultsClick.bind(this);
        this.#boundSetDrawerContentMaxHeight = debounce(this.#setDrawerContentMaxHeight.bind(this), 50);
    }
    connectedCallback() {
        this.#addSpinner();
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector("predictive-search-results");
        this.additionalContent = this.parentElement.querySelector(".js-search-content");
        this.searchTerm = "";
        this.abortController = null;
        this.#updateLoading();
        this.input.addEventListener("input", this.#boundOnChange);
        this.input.addEventListener("compositionstart", this.onCompositionStart);
        this.input.addEventListener("compositionend", this.onCompositionEnd);
        this.addEventListener("keyup", this.#boundOnKeyUp);
        this.addEventListener("keydown", PredictiveSearch.#onKeydown);
        if (this.isDropdown) {
            this.input.addEventListener("focus", this.#boundOnFocus);
            this.addEventListener("focusout", this.#boundOnFocusOut);
        }
        this.results.addEventListener("click", this.#boundOnResultsClick);
        if (this.type === "sidebar") {
            window.visualViewport.addEventListener("resize", this.#boundSetDrawerContentMaxHeight);
        }
    }
    disconnectedCallback() {
        this.scrollStyle.close();
        if (this.abortController) this.abortController.abort();
        this.input.removeEventListener("input", this.#boundOnChange);
        this.input.removeEventListener("compositionstart", this.onCompositionStart);
        this.input.removeEventListener("compositionend", this.onCompositionEnd);
        this.removeEventListener("keyup", this.#boundOnKeyUp);
        this.removeEventListener("keydown", PredictiveSearch.#onKeydown);
        if (this.isDropdown) {
            this.input.removeEventListener("focus", this.#boundOnFocus);
            this.removeEventListener("focusout", this.#boundOnFocusOut);
        }
        this.results.removeEventListener("click", this.#boundOnResultsClick);
        if (this.type === "sidebar") {
            window.visualViewport.removeEventListener("resize", this.#boundSetDrawerContentMaxHeight);
        }
    }
    getQuery() {
        return this.input.value.trim();
    }
    #onKeyup(event) {
        if (this.isComposing || event.isComposing || event.keyCode === 229) return;
        if (!this.getQuery().length) this.close(true);
        event.preventDefault();
        switch (event.code) {
          case "ArrowUp":
            this.switchOption("up");
            break;

          case "ArrowDown":
            this.switchOption("down");
            break;

          case "Enter":
            this.selectOption();
            break;

          default:
            break;
        }
    }
    static #onKeydown(event) {
        if (event.isComposing || event.keyCode === 229) return;
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
            event.preventDefault();
        }
    }
    #onChange(event) {
        if (this.isComposing || event?.isComposing) return;
        if (this.abortController) this.abortController.abort();
        const searchTerm = this.getQuery();
        this.searchTerm = searchTerm;
        if (!searchTerm.length) {
            this.close();
            this.#updateLoading();
            return;
        }
        this.getSearchResults(searchTerm);
    }
    #onFocus() {
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length) return;
        if (this.searchTerm !== currentSearchTerm) {
            this.#onChange();
        } else if (this.getAttribute("results") === "true") {
            this.open();
        } else {
            this.getSearchResults(this.searchTerm);
        }
    }
    #onFocusOut() {
        if (this.abortController) this.abortController.abort();
        this.#updateLoading();
        setTimeout((() => {
            if (!this.contains(document.activeElement)) this.close();
        }));
    }
    #onResultsClick(e) {
        const link = e.target.closest("a");
        if (!link) return;
        const searchUrl = this.getAttribute("search-url");
        const [linkPath] = link.getAttribute("href").split("?");
        if (searchUrl && searchUrl === linkPath) return;
        const searchForm = link.closest("search-form");
        if (searchForm) {
            searchForm.addFormStateToRecentSearches();
        }
    }
    async getSearchResults(searchTerm) {
        if (this.isComposing || !searchTerm) return;
        if (this.abortController) this.abortController.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.#updateLoading(true);
        try {
            const searchUrl = new URL(this.getAttribute("search-url") || "/search", window.location.origin);
            if (searchUrl.origin !== window.location.origin) throw new Error("Invalid search origin");
            searchUrl.search = "";
            searchUrl.hash = "";
            let features = {};
            try {
                features = JSON.parse(document.getElementById("shopify-features")?.textContent || "{}");
            } catch (_) {}
            const locale = String(window.Shopify?.locale || document.documentElement.lang || "").toLowerCase();
            let useRegularSearch = features.predictiveSearch === false || /^ja(?:-|$)/.test(locale);
            const request = async (regularSearch) => {
                const url = new URL(searchUrl.href);
                if (!regularSearch) url.pathname = url.pathname.replace(/\/$/, "") + "/suggest";
                url.searchParams.set("q", searchTerm);
                url.searchParams.set("section_id", regularSearch ? "search-preview-fallback" : "predictive-search");
                if (regularSearch) {
                    url.searchParams.set("type", "product");
                    url.searchParams.set("options[prefix]", "last");
                }
                return fetch(url.href, { signal: controller.signal, credentials: "same-origin", mode: "same-origin" });
            };
            let response = await request(useRegularSearch);
            if (response.status === 417 && !useRegularSearch) {
                useRegularSearch = true;
                response = await request(true);
            }
            if (!response.ok) throw new Error("Search preview unavailable");
            const text = await response.text();
            if (controller.signal.aborted || this.abortController !== controller || this.getQuery() !== searchTerm || !this.isConnected || this.isComposing) return;
            const parsed = new DOMParser().parseFromString(text, "text/html");
            const section = parsed.querySelector(useRegularSearch ? "#shopify-section-search-preview-fallback" : "#shopify-section-predictive-search");
            if (!section || !section.querySelector(".predictive-search")) throw new Error("Missing search preview section");
            this.results.innerHTML = section.innerHTML
                .replaceAll('id="predictive-search', `id="${this.type}-predictive-search`)
                .replaceAll('aria-labelledby="predictive-search', `aria-labelledby="${this.type}-predictive-search`);
            if (this.isDropdown) this.results.querySelector(".predictive-search__empty-heading")?.remove();
            this.input.setAttribute("aria-controls", `${this.type}-predictive-search-results`);
            this.input.setAttribute("aria-owns", `${this.type}-predictive-search-results`);
            this.input.setAttribute("aria-activedescendant", "");
            this.open();
        } catch (error) {
            if (error.name === "AbortError" || controller.signal.aborted || this.abortController !== controller) return;
            this.close();
        } finally {
            if (this.abortController === controller) {
                this.#updateLoading();
                this.abortController = null;
            }
        }
    }
    open() {
        this.setAttribute("open", true);
        this.input.setAttribute("aria-expanded", "true");
        this.spinner.setAttribute("hidden", "true");
        this.results.removeAttribute("hidden");
        if (this.additionalContent) this.additionalContent.setAttribute("hidden", true);
        this.scrollStyle.open();
    }
    close() {
        this.scrollStyle.close();
        this.removeAttribute("open");
        this.input.setAttribute("aria-expanded", "false");
        this.spinner.setAttribute("hidden", "true");
        this.results.setAttribute("hidden", "true");
        if (this.additionalContent) this.additionalContent.removeAttribute("hidden");
    }
    switchOption(direction) {
        if (!this.getAttribute("open")) return;
        const moveUp = direction === "up";
        let selectedElement = this.querySelector('[aria-selected="true"]');
        const allVisibleElements = Array.from(this.querySelectorAll("predictive-search-results li, button.predictive-search__btn"))
            .filter((element) => element.getClientRects().length > 0);
        if (selectedElement && !allVisibleElements.includes(selectedElement)) {
            selectedElement.setAttribute("aria-selected", "false");
            selectedElement = null;
            this.input.setAttribute("aria-activedescendant", "");
        }
        let activeElementIndex = 0;
        if (moveUp && !selectedElement) return;
        let selectedElementIndex = -1;
        let i = 0;
        while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
            if (allVisibleElements[i] === selectedElement) {
                selectedElementIndex = i;
            }
            i += 1;
        }
        if (!moveUp && selectedElement) {
            activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
        } else if (moveUp) {
            activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
        }
        if (activeElementIndex === selectedElementIndex) return;
        const activeElement = allVisibleElements[activeElementIndex];
        if (!activeElement) return;
        activeElement.setAttribute("aria-selected", true);
        if (selectedElement) selectedElement.setAttribute("aria-selected", false);
        this.input.setAttribute("aria-activedescendant", activeElement.id);
        const resultsContainer = this.querySelector(".predictive-search__panels");
        if (resultsContainer.offsetHeight < resultsContainer.scrollHeight && !activeElement.classList.contains("predictive-search__btn")) {
            if (activeElement.getBoundingClientRect().top < resultsContainer.getBoundingClientRect().top) {
                resultsContainer.scrollTo({
                    top: activeElement.offsetTop,
                    behavior: "instant"
                });
            }
            if (activeElement.getBoundingClientRect().bottom > resultsContainer.getBoundingClientRect().bottom - 72) {
                resultsContainer.scrollTo({
                    top: activeElement.offsetTop - (resultsContainer.offsetHeight - activeElement.offsetHeight - 72),
                    behavior: "instant"
                });
            }
        }
    }
    selectOption() {
        const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
        if (selectedOption && selectedOption.getClientRects().length > 0) selectedOption.click();
    }
    #addSpinner() {
        const spinner = document.createElement("div");
        spinner.classList.add("search-form__loader");
        spinner.setAttribute("hidden", true);
        spinner.innerHTML = `\n      <div class="theme-spinner theme-spinner--small">\n        <div class="theme-spinner__border"></div>\n        <div class="theme-spinner__border"></div>\n        <div class="theme-spinner__border"></div>\n        <div class="theme-spinner__border"></div>\n      </div>\n    `;
        this.spinner = spinner;
        this.querySelector(".search__form-input-wrapper").append(spinner);
    }
    #updateLoading(isLoading = false) {
        if (isLoading) {
            this.classList.add("is-loading");
            if (this.spinner) this.spinner.removeAttribute("hidden");
        } else {
            this.classList.remove("is-loading");
            if (this.spinner) this.spinner.setAttribute("hidden", true);
        }
    }
    #setDrawerContentMaxHeight() {
        const searchDraw = this;
        if (searchDraw && searchDraw.closest(".modal__content")) {
            const sidebarSearchForm = searchDraw.querySelector(".search__form-input-wrapper");
            if (window.visualViewport.height < window.innerHeight) {
                searchDraw.style.setProperty("--max-content-height", `${window.visualViewport.height - (sidebarSearchForm ? sidebarSearchForm.offsetHeight : 0)}px`);
            } else {
                searchDraw.style.removeProperty("--max-content-height");
            }
        }
    }
}

/* Visual-only preview scroll styling. No fetches or search-state changes. */
class SearchPreviewScrollStyle {
    constructor(host) {
        this.host = host;
        this.onScroll = () => { this.move(); this.reveal(); };
        this.onResize = () => this.update();
    }
    open() {
        this.close();
        try {
            const panel = this.host.results.querySelector('.predictive-search__panels');
            if (!panel || !window.matchMedia) return;
            this.panel = panel;
            this.media = window.matchMedia('(max-width: 47.9374em)');
            const style = window.getComputedStyle(panel);
            const borders = (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
            const width = Math.max(0, panel.offsetWidth - panel.clientWidth - borders);
            this.previousWidth = panel.style.getPropertyValue('--preview-native-scrollbar-width');
            this.previousPriority = panel.style.getPropertyPriority('--preview-native-scrollbar-width');
            if (width > 0) panel.style.setProperty('--preview-native-scrollbar-width', `${width}px`);
            panel.setAttribute('data-preview-scroll-style', '');
            panel.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onResize, { passive: true });
            if (typeof ResizeObserver === 'function') {
                this.observer = new ResizeObserver(this.onResize);
                this.observer.observe(panel);
                if (panel.firstElementChild) this.observer.observe(panel.firstElementChild);
            }
            this.update();
            this.reveal();
        } catch (_) { this.close(); }
    }
    update() {
        if (!this.panel) return;
        try {
            if (!this.media.matches) {
                this.panel.removeAttribute('data-preview-mobile-scroll');
                this.indicator?.remove();
                this.indicator = null;
                clearTimeout(this.timer);
                return;
            }
            this.panel.setAttribute('data-preview-mobile-scroll', '');
            let created = false;
            if (!this.indicator) {
                this.indicator = document.createElement('span');
                this.indicator.className = 'search-preview-scroll-indicator';
                this.indicator.setAttribute('aria-hidden', 'true');
                this.host.results.append(this.indicator);
                created = true;
            }
            const track = Math.max(0, this.panel.clientHeight - 6);
            this.maxScroll = Math.max(0, this.panel.scrollHeight - this.panel.clientHeight);
            this.indicator.style.display = this.maxScroll > 1 && track > 0 ? '' : 'none';
            const thumb = Math.min(track, Math.max(24, track * this.panel.clientHeight / Math.max(1, this.panel.scrollHeight)));
            this.travel = Math.max(0, track - thumb);
            const panelTop = this.panel.getBoundingClientRect().top;
            const resultsTop = this.host.results.getBoundingClientRect().top;
            this.indicator.style.top = `${panelTop - resultsTop - this.host.results.clientTop + 3}px`;
            this.indicator.style.height = `${thumb}px`;
            this.move();
            if (created) this.reveal();
        } catch (_) { this.close(); }
    }
    move() {
        if (!this.indicator || !this.panel) return;
        const ratio = this.maxScroll > 0 ? Math.max(0, Math.min(1, this.panel.scrollTop / this.maxScroll)) : 0;
        this.indicator.style.transform = `translateY(${ratio * this.travel}px)`;
    }
    reveal() {
        if (!this.indicator) return;
        this.indicator.classList.add('is-visible');
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.indicator?.classList.remove('is-visible'), 3500);
    }
    close() {
        clearTimeout(this.timer);
        this.observer?.disconnect();
        this.observer = null;
        if (this.panel) {
            this.panel.removeEventListener('scroll', this.onScroll);
            this.panel.removeAttribute('data-preview-mobile-scroll');
            this.panel.removeAttribute('data-preview-scroll-style');
            if (this.previousWidth) this.panel.style.setProperty('--preview-native-scrollbar-width', this.previousWidth, this.previousPriority);
            else this.panel.style.removeProperty('--preview-native-scrollbar-width');
        }
        window.removeEventListener('resize', this.onResize);
        this.indicator?.remove();
        this.indicator = null;
        this.panel = null;
    }
}

customElements.define("predictive-search", PredictiveSearch);
